import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, HStack, Icon, Badge, VStack , Card, CardHeader, CardBody } from "@chakra-ui/react";
import { FiTool } from "react-icons/fi";

import TablePagination from "../../Components/Pagination/TablePagination";
import TicketsTableRow from "../../Components/TableRows/TicketsTableRow";
import { selectActiveProperties } from "../../redux/user/userSlice";
import { getPropertyAddress } from "../../utils/verifier";

export default function Tickets() {
	const textColor = useColorModeValue("gray.700", "white");
	const [tickets, setTickets] = useState([]);
	const properties = useSelector(selectActiveProperties);
	const name = useSelector((state) => state.user.user?.name);
	const email = useSelector((state) => state.user.user?.email);
	const phoneNumber = useSelector((state) => state.user.user?.phoneNumber);
	const { t } = useTranslation();

	// Pagination
	const ITEMS_PER_PAGE = 10;
	const [currentPage, setCurrentPage] = useState(1);
	const totalTickets = tickets?.length || 0;
	const totalPages = Math.ceil(totalTickets / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = startIndex + ITEMS_PER_PAGE;
	const paginatedTickets = tickets?.slice(startIndex, endIndex);

	useEffect(() => {
		if (!properties) return;
		const t = properties
			.map((p) =>
				p.tickets.map((t) => {
					return { ...t, address: getPropertyAddress(p), propertyId: p.id, owner: p.owner };
				})
			)
			.flat();

		// sort the tickets based on status and if the status is the same, sort them based on the date they were added
		t.sort((a, b) => {
			if (a.status === b.status) {
				return new Date(b.addedDate) - new Date(a.addedDate);
			}
			// if the status is not the same, sort them based on the status. the status can be new, pending, resolved
			if (a.status === "new") return -1;
			if (b.status === "new") return 1;
			if (a.status === "pending") return -1;
			if (b.status === "pending") return 1;
			if (a.status === "resolved") return -1;
			if (b.status === "resolved") return 1;
			return 0;
		});
		setTickets(t);
	}, [properties]);

	return (
		<Flex direction={"column"}>
			<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
				<CardHeader>
					<HStack spacing={3} justify="space-between">
						<HStack spacing={3}>
							<Icon as={FiTool} w={6} h={6} color="blue.500" />
							<Text fontSize="2xl" color={textColor} fontWeight="bold">
								{t("tickets.tickets")}
							</Text>
						</HStack>
						{tickets?.length > 0 && (
							<HStack spacing={2}>
								<Badge variant="subtle" colorScheme="blue" fontSize="sm">
									{totalTickets} {t("tickets.tickets").toLowerCase()}
								</Badge>
								{totalPages > 1 && (
									<Text fontSize="sm" color="gray.500">
										{t("table.page")} {currentPage} {t("table.of")} {totalPages}
									</Text>
								)}
							</HStack>
						)}
					</HStack>
				</CardHeader>
				<CardBody>
					<VStack spacing={4} align="stretch">
						<Table variant="simple" color={textColor} size="sm">
							<Thead>
								<Tr my=".8rem" pl="0px">
									<Th>{t("table.addedDate")}</Th>
									<Th>{t("table.name")}</Th>
									<Th pl="0px">{t("table.address")}</Th>
									<Th>{t("table.status")}</Th>
									<Th>{t("table.title")}</Th>
									<Th>{t("table.author")}</Th>
									<Th></Th>
								</Tr>
							</Thead>
							<Tbody>
								{paginatedTickets?.map((t, index) => (
									<TicketsTableRow key={index} row={t} author={{ name: name, phoneNumber, email }} owner={t.owner} />
								))}
								{tickets?.length === 0 && (
									<Tr>
										<Td colSpan={7}>
											<Text color="gray.500" textAlign="center">
												{t("table.noData")}
											</Text>
										</Td>
									</Tr>
								)}
							</Tbody>
						</Table>
						<TablePagination
							currentPage={currentPage}
							totalPages={totalPages}
							totalItems={totalTickets}
							onPageChange={setCurrentPage}
							itemsName={t("tickets.tickets")}
						/>
					</VStack>
				</CardBody>
			</Card>
		</Flex>
	);
}
